import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/brand.css";
import { NAV_LINKS, ROTATING, SERVICES, AI_SOLUTIONS, INDUSTRIES, PROOFS, CASE_STUDIES, INSIGHTS, CONTACT, FAQ } from "../mock";
import { Card } from "../components/ui/card";
import { Menu } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const CAL_URL = "https://calendar.app.google/VV95bVbHUeUbi4756";
const QUIZ_URL = "https://ddchatbot.netlify.app/";
const HEADER_LOGO_URL = "https://customer-assets.emergentagent.com/job_dd-transform-hub/artifacts/badla7f2_Options%205-transparent%20background%20landscape%20copy%20%282%29.png";
const FOOTER_LOGO_URL = "https://customer-assets.emergentagent.com/job_dd-transform-hub/artifacts/apitj3py_Options%205-transparent%20background%20copy%20%281%29.png";

function useReveal() {
  const refs = useRef([]);
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); });
    }, { threshold: 0.15 });
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);
  const setRef = (el) => { if (el && !refs.current.includes(el)) refs.current.push(el); };
  return setRef;
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { const onScroll = () => setScrolled(window.scrollY > 4); onScroll(); window.addEventListener('scroll', onScroll); return () => window.removeEventListener('scroll', onScroll); }, []);
  return (
    <header className={`dd-header ${scrolled ? 'scrolled' : ''}`}>
      <a href="#main" className="skip-link">Skip to content</a>
      <div className="container dd-header-inner">
        <a href="#top" className="brand" aria-label="DD Consulting home">
          <img src={HEADER_LOGO_URL} height={72} className="brand-logo-landscape" alt="DD Consulting landscape logo" />
        </a>
        <div className="header-main">
          {/* Desktop CTAs - visible on desktop only */}
          <div className="header-ctas-desktop">
            <a href={CAL_URL} target="_blank" rel="noreferrer" className="btn-primary btn-base btn-sm" onClick={() => toast("Opening calendar...")}>
              BOOK 20‑MINUTE CONSULT
            </a>
            <a href={QUIZ_URL} target="_blank" rel="noreferrer" className="btn-secondary btn-base btn-sm" onClick={() => toast("Launching quiz...")}>
              AI READINESS QUIZ
            </a>
          </div>
          <div className="header-ctas">
            <button aria-label="Open menu" onClick={() => setOpen(v=>!v)} className="btn-secondary" style={{display:'inline-flex'}}>
              <Menu size={18} />
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="container" style={{paddingBottom:12}}>
          <nav aria-label="Mobile" className="hamburger-menu" style={{display:'grid', gap:12, paddingBottom:12}}>
            {NAV_LINKS.map(n => (
              <a key={n.label} href={n.href} className="nav-link" onClick={()=>setOpen(false)} style={{display:'block'}}>{n.label}</a>
            ))}
            {/* CTAs only show in hamburger menu on mobile/tablet */}
            <div className="header-ctas-mobile">
              <a href={CAL_URL} target="_blank" rel="noreferrer" className="btn-primary btn-base btn-lg" onClick={()=>setOpen(false)}>BOOK 20‑MINUTE CONSULT</a>
              <a href={QUIZ_URL} target="_blank" rel="noreferrer" className="btn-secondary btn-base btn-lg" onClick={()=>setOpen(false)}>AI READINESS QUIZ</a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="section" id="top" aria-label="Hero">
      <div className="container hero">
        <div style={{display:'grid', gap:24}} className="reveal visible">
          <h1 className="h1">Human‑centred strategy. Tech‑enabled results.</h1>
          <p className="hero-desc-secondary" style={{maxWidth:880}}>
            DD Consulting partners with organisations in healthcare, aged care &amp; disability, not‑for‑profit, legal, education and startups to navigate digital transformation, cloud, AI‑driven insights and change.
          </p>
          <div className="hero-cta">
            <a className="btn-primary btn-base btn-lg" href={CAL_URL} target="_blank" rel="noreferrer" onClick={() => toast("Opening calendar...")}>BOOK A 20‑MINUTE CONSULT</a>
            <a className="btn-secondary-white btn-base btn-lg" href={QUIZ_URL} target="_blank" rel="noreferrer" onClick={() => toast("Launching quiz...")}>AI READINESS QUIZ</a>
          </div>
        </div>
        {/* Hero cards in stair pattern - desktop only */}
        <div className="hero-cards-container desktop-only" aria-hidden="true">
          <div className="hero-cards-stair">
            <div className="hero-card-stair step-1">
              <div className="card-text">
                <small><span className="card-from-to">From</span> Fragmented systems <span className="card-from-to">to</span></small>
                <h3>UNIFIED OPERATIONS IN 90 DAYS.</h3>
              </div>
            </div>
            <div className="hero-card-stair step-2">
              <div className="card-text">
                <small><span className="card-from-to">From</span> Manual processes <span className="card-from-to">to</span></small>
                <h3>AI‑POWERED AUTOMATION.</h3>
              </div>
            </div>
            <div className="hero-card-stair step-3">
              <div className="card-text">
                <small><span className="card-from-to">From</span> Static events <span className="card-from-to">to</span></small>
                <h3>IMMERSIVE EXPERIENCES.</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Keep other sections unchanged
function Services() {
  const setRef = useReveal();
  return (
    <section className="section cream-panel" id="services">
      <div className="container">
        <div ref={setRef} className="reveal">
          <h2 className="h2" style={{color:'var(--brand-navy)'}}>Our capabilities</h2>
          <div className="card-grid" style={{marginTop:12}}>
            {SERVICES.map((s) => (
              <Card key={s.title} className="card">
                <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
                  <s.icon size={32} color="var(--brand-teal)" aria-hidden="true" />
                  <h3>{s.title}</h3>
                </div>
                <p>{s.desc}</p>
                <a href={s.link} className="link-underline" aria-label={`Learn more about ${s.title}`}>Learn more</a>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AIDataSection() {
  const setRef = useReveal();
  return (
    <section className="section" id="ai-data">
      <div className="container">
        <div ref={setRef} className="reveal" style={{display:'grid', gap:16}}>
          <h2 className="h2">AI &amp; Data Solutions</h2>
          <p className="body" style={{maxWidth:880, color:'var(--text-secondary)', opacity: 0.7}}>
            Most AI projects stumble on people and processes—not models. Our approach aligns teams, maps workflows and builds secure, compliant systems that deliver outcomes.
          </p>
          <div style={{display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:24}}>
            {AI_SOLUTIONS.map((a) => (
              <div key={a.name} className="rail-item" style={{borderRadius:12, padding:'24px'}}>
                <h3 style={{color:'white', fontSize:'1.5rem', fontWeight:'bold', marginBottom:'12px'}}>{a.name}</h3>
                <div style={{color:'var(--text-secondary)', fontSize:'0.9rem', lineHeight:'1.5', opacity: 0.8}}>{a.desc}</div>
              </div>
            ))}
          </div>
          <div style={{textAlign:'center', marginTop:'32px'}}><a className="btn-secondary btn-base" href="/ai-data">Learn more about AI &amp; Data</a></div>
        </div>
      </div>
    </section>
  );
}

function Industries() {
  const setRef = useReveal();
  return (
    <section className="section cream-panel" id="industries">
      <div className="container">
        <div ref={setRef} className="reveal">
          <h2 className="h2" style={{color:'var(--brand-navy)', marginBottom:12}}>Industries &amp; outcomes</h2>
          <div className="card-grid">
            {INDUSTRIES.map((i) => (
              <div key={i.slug} className="card">
                <h3>{i.name}</h3>
                <p>{i.outcome}</p>
                <a href={`/industries/${i.slug}`} className="link-underline">Explore sector</a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Custom hook for animated counters
function useAnimatedCounter(end, duration = 2000, shouldStart = false) {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) return;
    
    let startTime = null;
    const startCount = 0;
    
    const animate = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * (end - startCount) + startCount);
      
      setCount(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, shouldStart]);
  
  return count;
}

// Testimonial Carousel Component
function TestimonialCarousel({ testimonials }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Auto-slide functionality
  useEffect(() => {
    if (isHovered) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [testimonials.length, isHovered]);
  
  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };
  
  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };
  
  const getCardStyle = (index) => {
    const isCenter = index === currentIndex;
    const isPrevious = index === (currentIndex - 1 + testimonials.length) % testimonials.length;
    const isNext = index === (currentIndex + 1) % testimonials.length;
    
    if (isCenter) {
      return {
        transform: 'translateX(0%) scale(1)',
        opacity: 1,
        zIndex: 3,
        filter: 'blur(0px)'
      };
    } else if (isPrevious) {
      return {
        transform: 'translateX(-80%) scale(0.8)',
        opacity: 0.6,
        zIndex: 1,
        filter: 'blur(2px)'
      };
    } else if (isNext) {
      return {
        transform: 'translateX(80%) scale(0.8)',
        opacity: 0.6,
        zIndex: 1,
        filter: 'blur(2px)'
      };
    } else {
      return {
        transform: 'translateX(0%) scale(0.8)',
        opacity: 0,
        zIndex: 0,
        filter: 'blur(3px)'
      };
    }
  };
  
  return (
    <div style={{position: 'relative', height: '400px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
      {/* Navigation Arrows */}
      <button 
        onClick={prevSlide}
        style={{
          position: 'absolute',
          left: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.1)',
          border: '2px solid var(--brand-teal)',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          color: 'var(--brand-teal)',
          cursor: 'pointer',
          zIndex: 4,
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ‹
      </button>
      
      <button 
        onClick={nextSlide}
        style={{
          position: 'absolute',
          right: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(255,255,255,0.1)',
          border: '2px solid var(--brand-teal)',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          color: 'var(--brand-teal)',
          cursor: 'pointer',
          zIndex: 4,
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ›
      </button>
      
      {/* Testimonial Cards */}
      <div style={{position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        {testimonials.map((testimonial, index) => (
          <div
            key={index}
            onMouseEnter={() => index === currentIndex && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
              position: 'absolute',
              width: '500px',
              maxWidth: '90vw',
              padding: '32px',
              background: 'var(--brand-navy)',
              border: '2px solid var(--card-border)',
              borderRadius: '20px',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: index === currentIndex ? 'default' : 'pointer',
              ...getCardStyle(index)
            }}
            onClick={() => index !== currentIndex && setCurrentIndex(index)}
          >
            <div style={{display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px'}}>
              <img 
                src={testimonial.avatar}
                alt={testimonial.a}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: '3px solid var(--brand-teal)',
                  objectFit: 'cover'
                }}
              />
              <div style={{flex: 1}}>
                <div style={{fontSize: '2rem', color: 'var(--brand-teal)', marginBottom: '8px', lineHeight: 1}}>❝</div>
                <blockquote style={{
                  fontSize: '1.1rem',
                  fontStyle: 'italic',
                  color: 'white',
                  margin: 0,
                  lineHeight: 1.4
                }}>
                  {testimonial.q}
                </blockquote>
              </div>
            </div>
            
            <div style={{borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px'}}>
              <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: 'white', marginBottom: '4px'}}>
                {testimonial.a}
              </div>
              <div style={{color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '8px'}}>
                {testimonial.position} • {testimonial.company}
              </div>
              <div style={{
                display: 'inline-block',
                background: 'var(--brand-teal)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '500'
              }}>
                Service: {testimonial.service}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProofRail() {
  const setRef = useReveal();
  const [isVisible, setIsVisible] = useState(false);
  
  // Extract numbers and text for animation
  const statsData = [
    { number: 50, suffix: "M+", text: "projects delivered", prefix: "$" },
    { number: 97, suffix: "%", text: "client satisfaction", prefix: "" },
    { number: 26, suffix: "%", text: "average productivity increase", prefix: "" }
  ];
  
  const animatedNumbers = [
    useAnimatedCounter(50, 2000, isVisible),
    useAnimatedCounter(97, 2000, isVisible),
    useAnimatedCounter(26, 2000, isVisible)
  ];
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    
    const element = document.getElementById('why');
    if (element) observer.observe(element);
    
    return () => observer.disconnect();
  }, []);

  return (
    <section className="section" id="why">
      <div className="container">
        <h2 className="h2" style={{marginBottom:32, textAlign:'center'}}>Why choose us</h2>
        <div ref={setRef} className="reveal">
          {/* Stats Cards */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:24, marginBottom:48}}>
            {statsData.map((stat, i) => (
              <div key={i} className="card" style={{textAlign:'center', padding:'32px 24px', background:'linear-gradient(135deg, rgba(30,100,182,0.1), rgba(181,109,74,0.1))', border:'2px solid var(--card-border)'}}>
                <div style={{fontSize:'3rem', fontWeight:'bold', color:'var(--brand-teal)', marginBottom:'8px', fontFamily:'Montserrat'}}>
                  {stat.prefix}{animatedNumbers[i]}{stat.suffix}
                </div>
                <div style={{fontSize:'1.1rem', color:'white', fontWeight:'500'}}>{stat.text}</div>
              </div>
            ))}
          </div>
          
          {/* Testimonial Carousel */}
          <div style={{textAlign:'center', marginBottom:'24px'}}>
            <h3 style={{fontSize:'1rem', color:'var(--text-secondary)', marginBottom:'32px', opacity: 0.8}}>What our clients say</h3>
            <TestimonialCarousel testimonials={PROOFS.quotes} />
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const setRef = useReveal();
  return (
    <section className="section cream-panel" id="faq">
      <div className="container">
        <div ref={setRef} className="reveal" style={{maxWidth:900}}>
          <h2 className="h2" style={{marginBottom:12, color:'var(--brand-navy)'}}>Common questions</h2>
          <Accordion type="single" collapsible>
            {FAQ.map((f, idx) => (
              <AccordionItem key={idx} value={`q${idx}`}>
                <AccordionTrigger className="body" style={{color:'var(--brand-navy)'}}>{f.q}</AccordionTrigger>
                <AccordionContent className="body" style={{color:'var(--text-secondary)'}}>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function CaseInsights() {
  const setRef = useReveal();
  
  // Combine case studies and insights for display
  const blogPosts = [
    ...CASE_STUDIES.map(item => ({...item, summary: item.summary || item.excerpt})),
    ...INSIGHTS.map(item => ({...item, summary: item.excerpt || item.summary}))
  ];
  
  return (
    <section className="section" id="case-studies">
      <div className="container">
        <div className="reveal" ref={setRef} style={{display:'grid', gap:32}}>
          <div>
            <h2 className="h2" id="insights" style={{marginBottom:12}}>Case studies &amp; insights</h2>
            <p style={{color:'var(--text-secondary)', fontSize:'1.1rem', opacity:0.8}}>
              Explore our complete library of case studies, insights, and industry guides.
            </p>
          </div>
          
          {/* Blog Post Cards */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(350px, 1fr))', gap:24}}>
            {blogPosts.map((post) => (
              <div key={post.title} className="card" style={{overflow:'hidden', padding:0, display:'flex', flexDirection:'column', background:'white', border:'1px solid #e5e7eb'}}>
                {/* Post Image */}
                <div style={{position:'relative', height:'200px', overflow:'hidden'}}>
                  <img 
                    src={post.image}
                    alt={post.title}
                    style={{width:'100%', height:'100%', objectFit:'cover'}}
                  />
                  {/* Date Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {post.date}
                  </div>
                </div>
                
                {/* Card Content */}
                <div style={{padding:'20px', display:'flex', flexDirection:'column', flex:1}}>
                  {/* Tags */}
                  <div style={{display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap'}}>
                    {post.tags.map((tag, index) => (
                      <span
                        key={tag}
                        style={{
                          background: post.tagColors[index],
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '500'
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Title & Summary */}
                  <h3 style={{marginBottom:'8px', fontSize:'1.2rem', lineHeight:'1.3', color:'var(--brand-navy)'}}>{post.title}</h3>
                  <p style={{color:'#6b7280', marginBottom:'16px', fontSize:'0.9rem', lineHeight:'1.4', flex:1}}>
                    {post.summary}
                  </p>
                  
                  {/* CTA Button - Always at bottom */}
                  <a 
                    href={post.href} 
                    className="btn-secondary btn-base btn-lg" 
                    style={{
                      width:'100%', 
                      textAlign:'center', 
                      display:'block', 
                      padding:'12px 20px',
                      marginTop:'auto',
                      border:'2px solid var(--brand-teal)',
                      background:'transparent', 
                      color:'var(--brand-teal)'
                    }}
                  >
                    {post.type === 'case-study' ? 'READ CASE STUDY' : 'READ ARTICLE'}
                  </a>
                </div>
              </div>
            ))}
          </div>
          
          {/* Simple CTA Button */}
          <div style={{textAlign:'center'}}>
            <a 
              href="/insights" 
              className="btn-secondary btn-base btn-xl" 
              style={{padding:'16px 32px', fontSize:'1.1rem'}}
            >
              EXPLORE ALL INSIGHTS
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const onDownload = (e) => { e.preventDefault(); toast("Checklist download coming soon"); };
  return (
    <section className="section cta-strip" id="cta">
      <div className="container">
        <div className="cta-inner">
          <div>
            <div className="h2">Ready to transform? Let’s talk.</div>
            <div className="body">Book a 20‑minute consult or download our digital readiness checklist.</div>
          </div>
          <div style={{display:'flex', gap:12, alignItems:'center'}}>
            <a className="btn-primary btn-base btn-lg" href={CAL_URL} target="_blank" rel="noreferrer">BOOK A 20‑MINUTE CONSULT</a>
            <a className="btn-secondary btn-base btn-lg" href="#" onClick={onDownload}>DOWNLOAD CHECKLIST</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="section footer-wrap" id="contact">
      <div className="container footer-card">
        <div className="footer-grid">
          <div>
            <a href="#top" className="brand" style={{color:'var(--brand-navy)'}}>
              <img src={FOOTER_LOGO_URL} width={40} height={40} className="brand-logo" alt="DD Consulting logo" />
              <span style={{fontFamily:'Montserrat', fontWeight:800, color:'var(--brand-navy)'}}>DD Consulting</span>
            </a>
            <div className="body-dark" style={{marginTop:12}}>
              {CONTACT.address}<br/>
              Office: {CONTACT.office}<br/>
              Phone: {CONTACT.phone}<br/>
              <a className="footer-link" href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a><br/>
              <a className="footer-link" href={CONTACT.website} target="_blank" rel="noreferrer">ddconsult.com.au</a><br/>
              <a className="footer-link" href={CONTACT.linkedin} target="_blank" rel="noreferrer">linkedin.com/company/dd-consulting-au</a>
            </div>
          </div>
          <div>
            <div className="footer-title">Company</div>
            <ul style={{display:'grid', gap:8, marginTop:8}}>
              <li><a className="footer-link" href="#services">Services</a></li>
              <li><a className="footer-link" href="#industries">Industries</a></li>
              <li><a className="footer-link" href="#ai-data">AI &amp; Data</a></li>
              <li><a className="footer-link" href="#cloud">Cloud Solutions</a></li>
            </ul>
          </div>
          <div>
            <div className="footer-title">Resources</div>
            <ul style={{display:'grid', gap:8, marginTop:8}}>
              <li><a className="footer-link" href="#insights">Insights</a></li>
              <li><a className="footer-link" href="#case-studies">Case Studies</a></li>
              <li><a className="footer-link" href="#">Privacy Policy</a></li>
              <li><a className="footer-link" href="#">Terms</a></li>
            </ul>
          </div>
          <div>
            <div className="footer-title">Get in touch</div>
            <ul style={{display:'grid', gap:8, marginTop:8}}>
              <li><a className="footer-link" href={CAL_URL} target="_blank" rel="noreferrer">Book a consult</a></li>
              <li><a className="footer-link" href={QUIZ_URL} target="_blank" rel="noreferrer">AI Readiness Quiz</a></li>
            </ul>
          </div>
        </div>
        <div style={{marginTop:24, borderTop:'1px solid var(--card-border)', paddingTop:12, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
          <small className="body-dark">© {new Date().getFullYear()} DD Consulting. All rights reserved.</small>
          <a className="footer-link" href="#top">Back to top</a>
        </div>
      </div>
    </footer>
  );
}

export default function HomePage() {
  return (
    <main className="page" id="main">
      <Header />
      <Hero />
      <Services />
      <AIDataSection />
      <Industries />
      <CaseInsights />
      <ProofRail />
      <FAQSection />
      <CTA />
      <Footer />
      <Toaster richColors position="bottom-right" />
    </main>
  );
}